// External libraries
import axios from "axios";
import { Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// FontAwesome icons
import {
  faEdit,
  faLinkHorizontal,
  faLinkHorizontalSlash,
  faPenLine,
  faPrintMagnifyingGlass,
  faSplit,
  faTrashCan,
} from "@fortawesome/pro-duotone-svg-icons";
import { faPlusLarge } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Preact Signals
import { batch } from "@preact/signals-react";

// Kendo React components
import { filterBy, orderBy } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  Grid,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuSort,
} from "@progress/kendo-react-grid";
import { Checkbox, Signature } from "@progress/kendo-react-inputs";

// Internal components
import GroupWithSuspense from "../../Common/MicrosoftGraph/MgtGroup";
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import ConfirmDeleteDialog from "../../Notification/ConfirmDeleteDialog";
import ConfirmRevokeUrlDialog from "../../Notification/ConfirmRevokeUrlDialog";
import LoadingScreen from "../../Pages/LoadingScreen/LoadingScreen";
import { EntryPrintPreviewWindow } from "../Manage/EntryPrintPreviewWindow";
import { EditSplitEntryWindow } from "../Split/EditSplitEntryWindow";
import { SplitEntryWindow } from "../Split/SplitEntryWindow";
import {
  commentsData,
  commentsFieldName,
  CommentsViewWindow,
  showCommentsWindow,
} from "./CommentsViewWindow";
import {
  ImageLabelViewWindow,
} from "./ImageLabelViewWindow";

// Utilities
import { userBelongsToUserGroup } from "../../Utilities/AuthorizationUtil";
import { handleSignalRTrigger } from "../../Utilities/CommonUtils";
import {
  evaluateRowFormatting,
  getConnectionData,
  getEntryValues,
  isApprovalModeEntry,
  setInitialGalleryValues,
  validateStageStep,
} from "../../Utilities/EntryUtils";
import {
  businessUnitFilter,
  encryptEntryId,
  getRevisionCount,
  getTextWidth,
  userCanCreateEntry,
  userCanDeleteEntry,
  userCanEditEntry,
  userCanViewEntry,
} from "../../Utilities/ViewEntryUtils";
import {
  DateCell,
  DateTimeCell,
  DefaultCell,
  PeoplePickerCell,
  renderActionIdsCell,
  renderAttachmentCell,
  renderAuthorCell,
  renderCommentsCell,
  renderDateCell,
  renderDateRevisionsCell,
  renderDateTimeCell,
  renderGridColumnCell,
  renderHyperlinkCell,
  renderImageLabelCell,
  renderNumericCell,
  renderSignatureCell,
  renderTextAreaCell,
  renderTextFieldCell,
  renderTimeCell,
  TimeCell,
} from "./ViewEntryGridCellRenderers";

// Signals
import {
  allDatasets,
  allTemplates,
  getDatasetById,
  getEntriesByTemplateId,
  isAdmin,
  splitDeleteAndCreateEnabledDataset,
  splitEnabledDataset,
  userData,
  userEmail,
  userEntries,
  userTemplates,
  viewEntryHasColumnWidth,
} from "../../Common/Store/CommonSignals";
import { selectedHamburgerIndex } from "../../Navigation/HamburgerMenu/HamburgerMenuSignals";
import {
  disabledSections,
  entryId,
  entryValues,
  formErrors,
  galleryValues,
  initialCommentsLengths,
  isEntryPreview,
  readOnlyEntry,
  saveButtonText,
  selectedStep,
  selectedTab,
  showPrintPreviewWindow,
  steps,
  template,
  templateId,
  templateStageSteps,
} from "../Manage/EditEntrySignals";
import {
  currentEntryId,
  currentTemplateId,
  selectedEntry,
  showDeleteEntryDialog,
  showEditSplitEntryWindow,
  showRevokeUrlDialog,
  showSplitEntryWindow,
  viewEntryGridFilter,
} from "./ViewEntrySignals";

// Renders the column menu with sort and filter options for grid columns
const ColumnMenu = (event) => {
  return (
    <div>
      <GridColumnMenuSort {...event} />
      <GridColumnMenuFilter
        {...event}
        expanded={true}
      />
    </div>
  );
};

// Renders a grid row with conditional formatting based on template properties
const EntryGridRow = (event, selectedTemplate) => {
  const rowData = event.dataItem;
  const rowStyles = selectedTemplate?.properties?.ConditionalRowFormatting
    ? evaluateRowFormatting(rowData, selectedTemplate.properties.ConditionalRowFormatting)
    : null;
  return (
    <tr
      {...event.trProps}
      style={rowStyles}
    >
      {event.children}
    </tr>
  );
};

// Renders the expandable detail view for a grid row, showing entry fields and child templates
const DetailComponent = (event) => {
  const dataItem = event.dataItem.originalItem;
  const templateStructure = event.template;
  const actionId = dataItem.actionId;
  const userEmail = userData.value?.userEmail;
  const userIsAdmin = isAdmin.value;
  const navigate = useNavigate();

  //Get all of the name and value pairs from the current entry
  const entryFields = {};
  const revisionCount = {};
  dataItem.sections.forEach((section) => {
    section.fields.forEach((field) => {
      entryFields[field.fieldName] = field.value;
      if (field.properties["RevisionCount"]) {
        revisionCount[field.fieldName] = field.revisionCount;
      }
    });
  });

  //Use the template structure for the entry
  // Iterate over sections
  const sections = templateStructure.sections.map((section) => {
    // Extract fields from the section
    const fields = section.fields.filter((field) => field.properties["IsDetail"]);

    return fields;
  });

  // Flatten the array
  const detailFields = [].concat(...sections);

  // Renders a grid with entries from the child template linked to this entry
  const renderChildTemplate = () => {
    if (templateStructure.properties?.ChildTemplate?.id) {
      const childTemplate = userTemplates.value.find(
        (template) => template.id === templateStructure.properties.ChildTemplate.id
      );
      // // Get entries from the child template
      const childTemplateEntries =
        userEntries.value?.[templateStructure.properties.ChildTemplate.id] || [];

      // // Get the connected field name
      const connectedFieldName = templateStructure.properties.ConnectedField?.fieldName;

      // // Filter child entries where the connected field matches the current entry's field value
      const matchingChildEntries = childTemplateEntries.filter((childEntry) => {
        return childEntry.sections.some((section) =>
          section.fields.some(
            (childField) =>
              childField.fieldName === connectedFieldName &&
              childField.value === entryFields[connectedFieldName]
          )
        );
      });

      if (matchingChildEntries.length === 0) {
        return;
      }

      // Create columns for the child template grid
      const childTemplateColumns = childTemplate.sections.flatMap((section) =>
        section.fields
          .filter(
            (childField) => !childField.properties?.IsDetail && !childField.properties?.HideColumn
          )
          .map((childField) => ({
            fieldName: childField.fieldName,
            fieldType: childField.fieldType,
            fieldProperties: childField.properties,
          }))
      );

      // Flatten child entries for grid display
      const flattenedChildEntries = matchingChildEntries.map((entry) => {
        let flattenedItem = { entryId: entry.id, originalItem: entry };
        entry.sections.forEach((section) => {
          section.fields.forEach((childField) => {
            if (!childField.fieldType.includes("_isDetail")) {
              flattenedItem[childField.fieldName] = childField.value;
            }
          });
        });
        return flattenedItem;
      });

      // Create columns for the child entries
      const childColumns = childTemplateColumns.map((field) => {
        if (field.fieldType?.includes("PeoplePicker")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => {
                  try {
                    const personObject = JSON.tryParse(event.dataItem[field.fieldName]);
                    return personObject[0]?.id ? (
                      <td>
                        <Suspense fallback={<div>Loading...</div>}>
                          {personObject.map((person) => (
                            <div
                              key={person.id}
                              title={person.displayName}
                            >
                              {person.displayName}
                            </div>
                          ))}
                        </Suspense>
                      </td>
                    ) : (
                      <td></td>
                    );
                  } catch (e) {
                    console.error(e);
                    return <td></td>;
                  }
                },
              }}
            />
          );
        } else if (field.fieldType?.includes("GroupPicker")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => {
                  try {
                    const personObject = JSON.tryParse(event.dataItem[field.fieldName]);
                    return personObject[0]?.id ? (
                      <td>
                        <Suspense fallback={<div>Loading...</div>}>
                          {personObject.map((person) => (
                            <tr
                              key={person.id}
                              title={person.displayName}
                            >
                              <GroupWithSuspense
                                key={person.id}
                                groupId={person.id}
                              />
                            </tr>
                          ))}
                        </Suspense>
                      </td>
                    ) : (
                      <td></td>
                    );
                  } catch (e) {
                    console.error(e);
                    return <td />;
                  }
                },
              }}
            />
          );
        } else if (field.fieldType === "FileUpload") {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={150}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => renderAttachmentCell({ event, field }),
              }}
            />
          );
        } else if (field.fieldType === "ImageLabel") {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={150}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => renderImageLabelCell(event, field),
              }}
            />
          );
        } else if (field.fieldType === "Hyperlink") {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => renderHyperlinkCell({ event, field }),
              }}
            />
          );
        } else if (field.fieldType?.includes("DateTime")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => DateTimeCell(event, field),
              }}
            />
          );
        } else if (field.fieldType?.includes("Date")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={150}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => DateCell(event, field),
              }}
            />
          );
        } else if (field.fieldType?.includes("Time")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={150}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => TimeCell(event, field),
              }}
            />
          );
        } else if (field.fieldType?.includes("ContinuousComments")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => {
                  try {
                    const commentsArr = JSON.tryParse(event.dataItem[field.fieldName], []);
                    return (
                      <td>
                        <Button
                          onClick={() => {
                            commentsData.value = commentsArr;
                            commentsFieldName.value = field.fieldName;
                            showCommentsWindow.value = true;
                          }}
                          disabled={!Array.isArray(commentsArr) || commentsArr.length === 0}
                        >
                          View Comments ({commentsArr.length})
                        </Button>
                      </td>
                    );
                  } catch (e) {
                    console.error(e);
                    return <td></td>;
                  }
                },
              }}
            />
          );
        } else if (field.fieldType?.includes("Signature")) {
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => {
                  try {
                    return (
                      <td>
                        <Signature
                          value={event.dataItem[field.fieldName]}
                          smooth={true}
                          maximizable={true}
                          hideLine={true}
                          valid={true}
                          readOnly={true}
                        />
                      </td>
                    );
                  } catch (e) {
                    console.error(e);
                    return <td></td>;
                  }
                },
              }}
            />
          );
        } else {
          // Default column for text fields and other types
          return (
            <GridColumn
              field={field.fieldName}
              title={field.fieldName}
              key={field.fieldName}
              width={200}
              columnMenu={ColumnMenu}
              cells={{
                data: (event) => DefaultCell(event, field),
              }}
            />
          );
        }
      });

      return (
        <div style={{ marginTop: "20px" }}>
          <h2>Related {childTemplate.templateName} Entries</h2>
          <Grid
            data={flattenedChildEntries}
            sortable={{
              allowUnsort: true,
              mode: "multiple",
            }}
            resizable={true}
            dataItemKey={"entryId"}
            style={{ height: "400px" }}
          >
            <GridColumn
              title="Actions"
              width={150}
              locked={true}
              cells={{
                data: (event) =>
                  renderIcons(userIsAdmin, childTemplate, userEmail, event, navigate),
              }}
            />
            {childColumns}
          </Grid>
        </div>
      );
    }
  };

  // Renders a date field in the detail view
  function renderDateField(field, fieldValue) {
    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong> {formatDate(fieldValue)}
        {renderRevisionCount(field.fieldName)}
      </p>
    );
  }

  // Renders a datetime field in the detail view
  function renderDateTimeField(field, fieldValue) {
    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong> {formatDateTime(fieldValue)}
        {renderRevisionCount(field.fieldName)}
      </p>
    );
  }

  // Renders a time field in the detail view
  function renderTimeField(field, fieldValue) {
    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong> {formatTime(fieldValue)}
      </p>
    );
  }

  // Renders a business unit field with optional action ID
  function renderBusinessUnitField(field, fieldValue) {
    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong> {fieldValue || null}
        {actionId && (
          <div>
            <strong>Action Id: </strong>
            {actionId}
          </div>
        )}
      </p>
    );
  }

  // Renders a person/group picker field showing display names
  function renderPersonPickerField(field, fieldValue) {
    if (fieldValue) {
      entryFields[field.fieldName] = JSON.tryParse(fieldValue);
    }
    const parsedValue = entryFields[field.fieldName];

    return (
      <p key={field.fieldName}>
        <Suspense fallback={<div>Loading...</div>}>
          {Array.isArray(parsedValue) &&
            parsedValue.map((person) => (
              <div
                key={person.id}
                title={person.displayName}
              >
                <strong>{field.fieldName}: </strong>
                {person.displayName}
              </div>
            ))}
        </Suspense>
      </p>
    );
  }

  // Renders a hyperlink field as a clickable link
  function renderHyperlinkField(field, eventFieldValue) {
    const linkData = JSON.tryParse(eventFieldValue);

    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong>{" "}
        <a
          href={linkData?.href}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "underline", color: "blue" }}
        >
          {linkData?.text}
        </a>
      </p>
    );
  }

  // Renders a comments field with a button to view comments
  function renderCommentsField(field, eventFieldValue) {
    const commentsArr = JSON.tryParse(eventFieldValue, []);

    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong>{" "}
        <Button
          onClick={() => {
            commentsData.value = commentsArr;
            commentsFieldName.value = field.fieldName;
            showCommentsWindow.value = true;
          }}
          disabled={!Array.isArray(commentsArr) || commentsArr.length === 0}
        >
          View Comments ({commentsArr.length})
        </Button>
      </p>
    );
  }

  // Renders a signature field as a read-only signature component
  function renderSignatureField(field, eventFieldValue) {
    return (
      <p
        key={field.fieldName}
        style={{ width: "10%" }}
      >
        <strong>{field.fieldName}:</strong>
        <Signature
          value={eventFieldValue}
          smooth={true}
          maximizable={true}
          hideLine={true}
          valid={true}
          readOnly={true}
        />
      </p>
    );
  }

  // Renders a default text field in the detail view
  function renderDefaultField(field, fieldValue) {
    return (
      <p key={field.fieldName}>
        <strong>{field.fieldName}:</strong>{" "}
        <label data-testid={field.fieldName.replace(/ /g, "_") + "_" + event.dataIndex}>
          {fieldValue}
        </label>
      </p>
    );
  }

  // Formats a date value to US locale string
  function formatDate(value) {
    return value
      ? new Date(value)?.toLocaleString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        })
      : null;
  }

  // Formats a datetime value to US locale string
  function formatDateTime(value) {
    return value
      ? new Date(value)?.toLocaleString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
      : null;
  }

  // Formats a time value to US locale string
  function formatTime(value) {
    return value
      ? new Date(value)?.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        })
      : null;
  }

  // Renders the revision count for a field if it exists
  function renderRevisionCount(fieldName) {
    return revisionCount[fieldName] ? (
      <div>
        <strong>{fieldName} Revision Count: </strong>
        {revisionCount[fieldName]}
      </div>
    ) : null;
  }

  // Routes field rendering to the appropriate renderer based on field type
  function renderField(field) {
    const fieldValue = entryFields[field.fieldName];
    const eventFieldValue = event.dataItem[field.fieldName];

    const fieldRenderers = {
      Date: () => renderDateField(field, fieldValue),
      DateTime: () => renderDateTimeField(field, fieldValue),
      Time: () => renderTimeField(field, fieldValue),
      BusinessUnit: () => renderBusinessUnitField(field, fieldValue),
      PeoplePicker: () => renderPersonPickerField(field, fieldValue),
      GroupPicker: () => renderPersonPickerField(field, fieldValue),
      Hyperlink: () => renderHyperlinkField(field, eventFieldValue),
      ContinuousComments: () => renderCommentsField(field, eventFieldValue),
      Signature: () => renderSignatureField(field, eventFieldValue),
    };

    const renderer = fieldRenderers[field.fieldType];
    return renderer ? renderer() : renderDefaultField(field, fieldValue);
  }

  return (
    <div>
      {renderChildTemplate()}
      <h2>Details</h2>
      {detailFields.map((field) => renderField(field))}
    </div>
  );
};

// Main component for viewing and managing entries in a grid format
const ViewEntry = () => {
  selectedHamburgerIndex.value = 2;
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(null);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadAllRecords, setLoadAllRecords] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const templates = userTemplates.value;
  const [businessFilteredData, setBusinessFilteredData] = useState([]);
  const [data, setData] = useState([]);
  const userEmail = userData.value?.userEmail;
  const userIsAdmin = isAdmin.value;
  const userBusinessUnit = userData.value.officeLocation;
  const [filteredGridData, setFilteredGridData] = useState([]);
  const allDatasetsValue = allDatasets.value;
  const hideActionsForSplitEntry =
    splitEnabledDataset.value?.values?.some((item) => item.value === templateId.value) &&
    !splitDeleteAndCreateEnabledDataset.value?.values?.some((item) => item.value === userEmail);
  const [page, setPage] = useState({ skip: 0, take: 25 });
  // Handles grid pagination changes
  const pageChange = (event) => {
    setPage({
      skip: event.page.skip,
      take: event.page.take,
    });
  };
  // Flattens nested entry data structure for grid display
  const flattenData = (data) => {
    if (!data) return [];

    const flattenField = (flattenedItem, field, item) => {
      if (!field.fieldType.includes("_isDetail")) {
        flattenedItem[field.fieldName] = field.value;
      }
      if (field?.properties?.ActionId) {
        flattenedItem["ActionId"] = item?.actionId;
      }
    };

    const flattenFields = (flattenedItem, fields, item) => {
      fields.forEach((field) => flattenField(flattenedItem, field, item));
    };

    const flattenSection = (flattenedItem, section, item) => {
      if (section.fields) {
        flattenFields(flattenedItem, section.fields, item);
      }
    };
    return data.map((item) => {
      let flattenedItem = { entryId: item.id, originalItem: item, expanded: item.expanded };
      if (item.sections) {
        item.sections.forEach((section) => flattenSection(flattenedItem, section, item));
      }
      if (item.entryAuthor) {
        flattenedItem.entryAuthor = item.entryAuthor;
      }
      return flattenedItem;
    });
  };

  useEffect(() => {
    templateId.value = templateId.value ?? params.get("templateId");

    if (templateId.value && templates?.length > 0) {
      const index = templates.findIndex((template) => template.id === templateId.value);
      if (index !== -1) {
        setSelectedTemplateIndex({
          value: index,
          label: templates[index].templateName,
          branch: templates[index].branch,
          id: templates[index].id,
          properties: templates[index].properties,
        });
      } else {
        console.log(`Template with id ${templateId.value} not found`);
      }
    }

    setIsLoading(false);
  }, [templates]);

  //get template data by selected index
  useEffect(() => {
    fetchTemplateData().catch((error) => {
      console.log(error);
    });
    if (selectedTemplateIndex?.id && !viewEntryGridFilter.value?.[selectedTemplateIndex?.id]) {
      viewEntryGridFilter.value = {
        ...viewEntryGridFilter.value,
        [selectedTemplateIndex?.id]: {
          sort: [],
          filter: { logic: "and", filters: [] },
        },
      };
    }
  }, [selectedTemplateIndex, userEntries.value?.[selectedTemplateIndex?.id]]);

  const fetchTemplateData = async () => {
    if (selectedTemplateIndex === null) return;
    try {
      setIsLoading(true);
      let entries = orderBy(userEntries.value?.[selectedTemplateIndex.id], [
        { field: "createdDate", dir: "desc" },
      ]);
      const template = templates.find((template) => template.id === selectedTemplateIndex.id);

      if (!userIsAdmin) {
        if (
          template.sharedPermission?.some(
            (sp) =>
              sp.entity.toLowerCase() === userEmail.toLowerCase() &&
              sp.permission === "ViewAuthorOnly"
          )
        ) {
          entries = entries.filter(
            (entry) => entry.entryAuthor.toLowerCase() === userEmail.toLowerCase()
          );
        }
      }
      setSelectedTemplate(template);
      setSelectedTemplateData(entries);
    } catch (error) {
      console.log("Error fetching template data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // #region Filter Template Select Options ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // If selectedTemplateData has fields with fieldType of Business unit, only show entries with matching business unit as the user
  useEffect(() => {
    viewEntryHasColumnWidth.value = false;
    if (selectedTemplateData && selectedTemplateData.length > 0 && !userIsAdmin) {
      let hasBusinessUnitField = false;
      const filteredData = selectedTemplateData.filter((entry) => {
        return entry.sections.some((section, sIndex) => {
          return section.fields.some((field, fIndex) => {
            if (selectedTemplate.owner.toLowerCase() === userEmail.toLowerCase()) return true;
            if (
              selectedTemplate.coOwners?.some((co) => {
                // Check individual co-owners
                if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
                  return true;
                }
                // Check if user belongs to a co-owner group
                if (co.isUserGroup && co.userGroupId) {
                  return userBelongsToUserGroup(userEmail, co.userGroupId);
                }
                return false;
              })
            )
              return true;
            if (field.fieldType === "BusinessUnit") {
              hasBusinessUnitField = true;
              if (selectedTemplate.sections?.[sIndex]?.fields?.[fIndex]?.properties?.FilterOverride)
                return true;
              return field.value === userBusinessUnit;
            }
            return false;
          });
        });
      });
      const finalData = hasBusinessUnitField ? filteredData : selectedTemplateData;
      setBusinessFilteredData(finalData);
    } else {
      setBusinessFilteredData(selectedTemplateData);
    }
  }, [selectedTemplateData]);

  // Create selectOptions array from templates
  const selectOptions = templates.map((template, index) => ({
    value: index,
    label: template.templateName,
    branch: template.branch,
    id: template.id,
    properties: template.properties,
  }));

  useEffect(() => {
    if (templates?.length > 0) {
      setData(selectOptions);
    }
  }, [templates]);

  // Update 'data' when selectOptions changes and data length is 0
  useEffect(() => {
    if (selectOptions.length > 0 && data.length === 0) {
      setData(selectOptions);
    }
  }, [data.length]);

  // Filters template select options based on search criteria
  const filterData = (filter) => {
    const data = selectOptions;
    return filterBy(data, filter);
  };

  // Handles filter change in template ComboBox
  const filterChange = (event) => {
    const newData = event.filter.value.length > 0 ? filterData(event.filter) : selectOptions;
    setData(newData);
  };

  // Loads additional records for the current template
  const loadMoreGridRecords = () => {
    getEntriesByTemplateId(templateId.value, "more");
  };

  // Loads all records for the current template
  const loadAllGridRecords = () => {
    if (!loadAllRecords) {
      getEntriesByTemplateId(templateId.value, "all");
    }
    setLoadAllRecords(true);
  };

  // #endregion ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  //#region UI Interactions
  // Handles template selection change and loads entries for the selected template
  async function handleSelectChange(event) {
    const selectedOption = event.target.value;
    templateId.value = selectedOption?.id;
    setLoadAllRecords(false);
    window.history.pushState({}, "", "?templateId=" + selectedOption?.id);
    await getEntriesByTemplateId(selectedOption?.id, "initial");
    if (selectedOption?.properties?.ChildTemplate?.id) {
      await getEntriesByTemplateId(selectedOption?.properties?.ChildTemplate?.id, "initial");
      await getEntriesByTemplateId(selectedOption?.properties?.ChildTemplate?.id, "all");
    }
    setSelectedTemplateIndex(selectedOption);
  }

  // Confirms and executes entry deletion
  const handleDeleteDialogConfirm = async () => {
    showDeleteEntryDialog.value = false;

    try {
      setIsLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Entry/${
          selectedEntry.value
        }?userId=${userEmail}&templateId=${selectedTemplateIndex.id}&templateSync=${
          selectedTemplateIndex.properties?.["TemplateSync"] ?? false
        }`
      );
      const tempEntries = { ...userEntries.value };
      tempEntries[selectedTemplateIndex.id] = tempEntries[selectedTemplateIndex.id].filter(
        (entry) => entry.id !== selectedEntry.value
      );
      userEntries.value = tempEntries;

      await handleSignalRTrigger("DELETE", "Entry", {
        id: selectedEntry.value,
        TemplateId: selectedTemplateIndex.id,
      });

      fetchTemplateData();
      setIsLoading(false);
    } catch (error) {
      console.log("Error deleting entry:", error);
      setIsLoading(false);
    }
  };

  // Confirms and executes URL revocation
  const handleRevokeUrlDialogConfirm = async () => {
    showRevokeUrlDialog.value = false;

    revokeLink();
  };

  // Extracts and renders filter badges from the current grid filters
  const extractFilters = (filters, templateId) => {
    const uniqueFields = new Set();

    return filters.map((filter, index) => {
      if (filter.filters) {
        return extractFilters(filter.filters, templateId);
      }

      if (uniqueFields.has(filter.field)) {
        return null;
      }
      uniqueFields.add(filter.field);

      return (
        <span
          key={templateId + "_" + index}
          className="badge"
        >
          {filter.field}
          <button
            className="badge-close"
            onClick={() => removeFilter(filter, templateId)}
          >
            ×
          </button>
        </span>
      );
    });
  };

  // Removes a specific filter from the grid filter state
  const removeFilter = (removedFilter, templateId) => {
    const removeFilterRecursively = (filters) => {
      return filters
        .map((filter) => {
          if (filter.filters) {
            const updatedSubFilters = removeFilterRecursively(filter.filters);
            return updatedSubFilters.length > 0 ? { ...filter, filters: updatedSubFilters } : null;
          }
          return filter.field === removedFilter.field ? null : filter;
        })
        .filter(Boolean);
    };

    viewEntryGridFilter.value = {
      ...viewEntryGridFilter.value,
      [templateId]: {
        ...viewEntryGridFilter.value[templateId],
        filter: {
          ...viewEntryGridFilter.value[templateId]?.filter,
          filters: removeFilterRecursively(viewEntryGridFilter.value[templateId]?.filter.filters),
        },
      },
    };
  };

  // Clears all filters and sorts from the grid
  const clearAllFilters = () => {
    viewEntryGridFilter.value = {
      ...viewEntryGridFilter.value,
      [selectedTemplateIndex.id]: {
        ...viewEntryGridFilter.value[selectedTemplateIndex.id],
        filter: { logic: "and", filters: [] },
        sort: [],
      },
    };
  };

  //#endregion UI Interactions

  // Calculates dynamic column width based on content
  const calculateWidth = (field) => {
    if (
      !userEntries.value[selectedTemplateIndex?.id] ||
      userEntries.value[selectedTemplateIndex?.id].length === 0
    )
      return;
    let calculatedWidth = 0;

    let entryFrame = flattenData(
      businessUnitFilter(
        userEntries.value[selectedTemplateIndex.id].slice(page.skip, page.skip + page.take),
        selectedTemplate,
        userIsAdmin,
        userEmail,
        userBusinessUnit
      ) || []
    );

    entryFrame.forEach((item) => {
      const size = getTextWidth(item[field.fieldName], "normal 14px sans-serif") + 100;
      const columnHeaderSize = getTextWidth(field.fieldName, "bold 14px sans-serif") + 75;

      const maxWidth =
        field?.fieldProperties?.ViewEntryColumnWidth === ""
          ? 500
          : field?.fieldProperties?.ViewEntryColumnWidth ?? 500;

      if (size > calculatedWidth) {
        calculatedWidth = size;
      }
      if (columnHeaderSize > calculatedWidth) {
        calculatedWidth = columnHeaderSize;
      }
      if (calculatedWidth > maxWidth) {
        calculatedWidth = maxWidth;
      }
    });
    return calculatedWidth;
  };

  // Creates a setter function for grid filter state by template ID
  const makeSetGridFilter = (templateIndexId) => (newFilter) => {
    viewEntryGridFilter.value = {
      ...viewEntryGridFilter.value,
      [templateIndexId]: {
        ...viewEntryGridFilter.value[templateIndexId],
        filter: newFilter,
      },
    };
  };

  const columns =
    selectedTemplateData && selectedTemplateData.length > 0
      ? selectedTemplate.sections
          .flatMap((section) => {
            const fields = section.fields
              .filter((field) => !field.properties?.IsDetail && !field.properties?.HideColumn)
              .map((field) => ({
                fieldName: `${field.fieldName}`,
                fieldLabel: `${field.properties.Label ?? field.fieldName}`,
                fieldType: field.fieldType,
                fieldProperties: field.properties,
              }));

            if (
              fields.some(
                (field) =>
                  field.fieldProperties?.ViewEntryColumnWidth && !viewEntryHasColumnWidth.value
              )
            ) {
              viewEntryHasColumnWidth.value = true;
            }

            return [...fields];
          })
          .map((field) => {
            const fieldType = field.fieldType;

            switch (true) {
              case fieldType?.includes("PeoplePicker") || fieldType?.includes("GroupPicker"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) => PeoplePickerCell(event, field),
                    }}
                  />,
                ];

              case fieldType === "FileUpload":
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderAttachmentCell({
                          event,
                          field,
                        }),
                    }}
                  />,
                ];

              case fieldType === "ImageLabel":
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={150}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) => renderImageLabelCell(event, field),
                    }}
                  />,
                ];

              case fieldType === "Hyperlink":
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderHyperlinkCell({
                          event,
                          field,
                        }),
                    }}
                  />,
                ];

              case fieldType?.includes("DateTime"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderDateTimeCell({
                          event,
                          field,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          updateRevisionCount,
                          gridFilter: viewEntryGridFilter.value[selectedTemplateIndex.id]
                            ?.filter || {
                            logic: "and",
                            filters: [],
                          },
                          setGridFilter: makeSetGridFilter(selectedTemplateIndex.id),
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                  field.fieldProperties?.["RevisionCount"] ? (
                    <GridColumn
                      field={field.fieldName + " Revisions"}
                      title={field.fieldName + " Revisions"}
                      key={field.fieldName + " Revisions"}
                      width={100}
                      columnMenu={ColumnMenu}
                      cells={{ data: (event) => renderDateRevisionsCell(event, field) }}
                    />
                  ) : null,
                ].filter(Boolean);

              case fieldType?.includes("Date"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderDateCell({
                          event,
                          field,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          updateRevisionCount,
                          gridFilter: viewEntryGridFilter.value[selectedTemplateIndex.id]
                            ?.filter || {
                            logic: "and",
                            filters: [],
                          },
                          setGridFilter: makeSetGridFilter(selectedTemplateIndex.id),
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                  field.fieldProperties?.["RevisionCount"] ? (
                    <GridColumn
                      field={field.fieldName + " Revisions"}
                      title={field.fieldName + " Revisions"}
                      key={field.fieldName + " Revisions"}
                      width={100}
                      columnMenu={ColumnMenu}
                      cells={{ data: (event) => renderDateRevisionsCell(event, field) }}
                    />
                  ) : null,
                ].filter(Boolean);

              case fieldType?.includes("Time"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderTimeCell({
                          event,
                          field,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          gridFilter: viewEntryGridFilter.value[selectedTemplateIndex.id]
                            ?.filter || {
                            logic: "and",
                            filters: [],
                          },
                          setGridFilter: makeSetGridFilter(selectedTemplateIndex.id),
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                ];

              case fieldType?.includes("BusinessUnit"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{ data: (event) => renderGridColumnCell({ event, field }) }}
                  />,
                  field.fieldProperties?.["ActionId"] ? (
                    <GridColumn
                      field={"ActionId"}
                      title={"Action Id"}
                      key={"ActionId"}
                      columnMenu={ColumnMenu}
                      width={calculateWidth(field)}
                      cells={{ data: (event) => renderActionIdsCell({ event, field }) }}
                    />
                  ) : null,
                ].filter(Boolean);

              case fieldType?.includes("TextArea"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderTextAreaCell({
                          event,
                          field,
                          allDatasets: allDatasetsValue,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                ];

              case fieldType?.includes("TextField"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldLabel}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderTextFieldCell({
                          event,
                          field,
                          allDatasets: allDatasetsValue,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                ];

              case fieldType?.includes("Numeric"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderNumericCell({
                          event,
                          field,
                          allDatasets: allDatasetsValue,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                ];

              case fieldType?.includes("ContinuousComments"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) => renderCommentsCell(event, field),
                    }}
                  />,
                ];

              case fieldType?.includes("Signature"):
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) => renderSignatureCell(event, field),
                    }}
                  />,
                ];

              default:
                return [
                  <GridColumn
                    field={field.fieldName}
                    title={field.fieldName}
                    key={field.fieldName}
                    width={calculateWidth(field)}
                    columnMenu={ColumnMenu}
                    cells={{
                      data: (event) =>
                        renderGridColumnCell({
                          event,
                          field,
                          allDatasets: allDatasetsValue,
                          userIsAdmin,
                          selectedTemplate,
                          userEmail,
                          filteredGridData,
                          setFilteredGridData,
                        }),
                    }}
                  />,
                ];
            }
          })
          .flat()
      : [];

  // Updates a specific entry in the business filtered data array
  function updateBusinessFilteredData(entry) {
    const index = businessFilteredData.findIndex((e) => e.id === entry.id);
    const newBusinessFilteredData = [...businessFilteredData];
    newBusinessFilteredData[index] = entry;
    setBusinessFilteredData(newBusinessFilteredData);
  }

  // Updates the revision count for a field when its value changes
  function updateRevisionCount(entry, targetField, dataItem, fieldValue) {
    const strippedFieldName = targetField.name.split("_").slice(0, -1).join("_");

    entry?.sections?.forEach((section) => {
      section?.fields?.forEach((field) => {
        if (field?.fieldName === strippedFieldName) {
          if (new Date(targetField.value) > new Date(field.value)) {
            field.revisionCount = Number(field.revisionCount) + 1;
            dataItem[field.fieldName + " Revisions"] = field.revisionCount;
          }
          field.value = fieldValue;
        }
      });
    });
    updateBusinessFilteredData(entry);

    return entry;
  }

  //#region Flatten template data ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  useEffect(() => {
    const fetchAndFilter = async () => {
      const currentTemplateFilters = viewEntryGridFilter.value?.[selectedTemplateIndex?.id] || {
        sort: [],
        filter: { logic: "and", filters: [] },
      };

      if (currentTemplateFilters.filter.filters.length > 0) {
        await getEntriesByTemplateId(templateId.value, "all");
        setLoadAllRecords(true);
      }

      const flattened = flattenData(userEntries.value?.[selectedTemplateIndex?.id]);
      setFilteredGridData(flattened);
    };

    fetchAndFilter();
  }, [
    businessFilteredData,
    viewEntryGridFilter.value?.[selectedTemplateIndex?.id]?.filter,
    viewEntryGridFilter.value?.[selectedTemplateIndex?.id]?.sort,
  ]);

  useEffect(() => {
    if (selectedTemplateIndex && userEntries.value?.[selectedTemplateIndex?.id]) {
      const flattenedData = flattenData(userEntries.value[selectedTemplateIndex?.id]);
      setFilteredGridData(flattenedData);
    }
  }, [selectedTemplateIndex, userEntries.value]);

  //#endregion ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

  return (
    <div>
      <div>
        <ComboBox
          name="txtEntrySearchBar"
          data={data}
          allowCustom={false}
          adaptive={true}
          adaptiveTitle="Template List"
          filterable={true}
          rounded="large"
          size="large"
          textField="label"
          valueField="value"
          groupField="branch"
          placeholder="Select a template for entry..."
          minLength={0}
          onChange={handleSelectChange}
          onFilterChange={filterChange}
          value={selectedTemplateIndex}
        />
      </div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div>
          <div>
            {selectedTemplateIndex && (
              <div className="page-content">
                <div className="view-entry-header-container">
                  {!hideActionsForSplitEntry && (
                    <Button
                      themeColor={"primary"}
                      className="k-mb-1 k-mt-2"
                      hidden={!userCanCreateEntry(userIsAdmin, selectedTemplate, userEmail)}
                      onClick={() => {
                        batch(() => {
                          galleryValues.value = [];
                          templateId.value = selectedTemplateIndex.id;
                          template.value = allTemplates.value.find(
                            (x) => x.id === selectedTemplateIndex.id
                          );
                          const updatedSections = template.value.sections.map(
                            (section, sIndex) => ({
                              ...section,
                              properties: section.properties || {},
                              fields: section.fields.map((field, fIndex) => {
                                const fieldType = field.fieldType;
                                let fieldValue;
                                if (fieldType === "BusinessUnit") {
                                  fieldValue = userData.value?.officeLocation;
                                } else if (fieldType === "Checkbox") {
                                  fieldValue = "false";
                                } else if (
                                  typeof field.properties?.DefaultValue === "boolean" &&
                                  fieldType.includes("Date")
                                ) {
                                  const newDate = new Date();
                                  if (field.properties?.HoursOnly) {
                                    newDate.setMinutes(0);
                                    newDate.setSeconds(0);
                                    newDate.setMilliseconds(0);
                                  }
                                  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
                                  const convertedDate = new Date(newDate.getTime() - timezoneOffset)
                                    .toISOString()
                                    .replace("Z", "");
                                  fieldValue = field.properties?.DefaultValue ? convertedDate : "";
                                } else if (fieldType === "Time") {
                                  const newDate = new Date();
                                  const timezoneOffset = new Date().getTimezoneOffset() * 60000;
                                  const convertedDate = new Date(newDate.getTime() - timezoneOffset)
                                    .toISOString()
                                    .replace("Z", "");
                                  fieldValue = field.properties?.DefaultValue ? convertedDate : "";
                                } else {
                                  fieldValue = field.properties?.DefaultValue ?? "";
                                }
                                if (fieldType === "Gallery") {
                                  const initialGalleryValues = setInitialGalleryValues({
                                    galleryName: field.fieldName,
                                    values: getConnectionData({
                                      field,
                                      Data: field.properties?.Data,
                                    }),
                                    fields: field.properties?.Fields,
                                    field: field,
                                  });

                                  entryValues.value = {
                                    ...entryValues.value,
                                    [field.fieldName]: initialGalleryValues,
                                  };

                                  const newTemplateView = JSON.tryParse(
                                    JSON.stringify(template.value)
                                  ); // Deep copy
                                  let newField = {
                                    ...newTemplateView.sections[sIndex].fields[fIndex],
                                    Values: initialGalleryValues,
                                  };
                                  newTemplateView.sections[sIndex].fields[fIndex] = newField;

                                  template.value = newTemplateView;

                                  return {
                                    ...field,
                                    value: "",
                                    properties: {
                                      ...field.properties,
                                      Values: initialGalleryValues,
                                    },
                                  };
                                }
                                if (field.properties?.["RevisionCount"]) {
                                  return {
                                    ...field,
                                    value: fieldValue,
                                    revisionCount: getRevisionCount(
                                      { ...template.value },
                                      field.fieldName
                                    ),
                                  };
                                }
                                return {
                                  ...field,
                                  value: fieldValue,
                                };
                              }),
                            })
                          );
                          template.value = { ...template.value, sections: updatedSections };
                          entryValues.value = getEntryValues(template.value);
                          entryId.value = null;
                          formErrors.value = {};
                          disabledSections.value = {};
                          selectedTab.value = 0;
                          selectedStep.value = 0;
                          saveButtonText.value = "Save";
                          initialCommentsLengths.value = {};
                          isEntryPreview.value = false;
                          steps.value =
                            templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
                          readOnlyEntry.value = false;
                        });

                        validateStageStep();
                        navigate(`/new-entry?templateId=${templateId.value}`);
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faPlusLarge}
                        style={{ marginRight: "5px" }}
                      />
                      New Entry
                    </Button>
                  )}{" "}
                  <span className="k-ml-3 k-fs-xl k-font-bold">
                    {
                      templates.find((template) => template.id === selectedTemplateIndex.id)
                        .templateName
                    }
                  </span>
                  <div className="view-entry-filters-container">
                    <Checkbox
                      checked={loadAllRecords}
                      label="Load all records"
                      onChange={() => {
                        loadAllGridRecords();
                      }}
                    />

                    {viewEntryGridFilter.value?.[selectedTemplateIndex.id]?.filter?.filters.length >
                      0 && (
                      <div>
                        <span
                          className="k-ml-4 k-mr-2"
                          style={{ fontWeight: "bold" }}
                        >
                          {extractFilters(
                            viewEntryGridFilter.value?.[selectedTemplateIndex.id]?.filter
                              ?.filters || [],
                            selectedTemplateIndex.id
                          )}
                        </span>

                        <Button
                          onClick={clearAllFilters}
                          className="k-mb-1 k-ml-1"
                        >
                          Clear all filters
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <Grid
                  key={viewEntryHasColumnWidth.value}
                  data={orderBy(
                    filterBy(
                      flattenData(
                        businessUnitFilter(
                          userEntries.value?.[selectedTemplateIndex.id],
                          selectedTemplate,
                          userIsAdmin,
                          userEmail,
                          userBusinessUnit
                        ) || []
                      ),
                      viewEntryGridFilter?.value?.[selectedTemplateIndex?.id]?.filter || {
                        logic: "and",
                        filters: [],
                      }
                    ),
                    viewEntryGridFilter?.value?.[selectedTemplateIndex?.id]?.sort || []
                  ).slice(page.skip, page.take + page.skip)}
                  sortable={{
                    allowUnsort: true,
                    mode: "multiple",
                  }}
                  sort={viewEntryGridFilter?.value?.[selectedTemplateIndex?.id]?.sort || []}
                  onSortChange={(e) => {
                    viewEntryGridFilter.value = {
                      ...viewEntryGridFilter?.value,
                      [selectedTemplateIndex?.id]: {
                        ...viewEntryGridFilter?.value?.[selectedTemplateIndex?.id],
                        sort: e.sort,
                      },
                    };
                  }}
                  filter={
                    viewEntryGridFilter?.value?.[selectedTemplateIndex?.id]?.filter || {
                      logic: "and",
                      filters: [],
                    }
                  }
                  onFilterChange={(e) => {
                    viewEntryGridFilter.value = {
                      ...viewEntryGridFilter?.value,
                      [selectedTemplateIndex?.id]: {
                        ...viewEntryGridFilter?.value?.[selectedTemplateIndex?.id],
                        filter: e.filter ?? { logic: "and", filters: [] },
                      },
                    };
                  }}
                  resizable={true}
                  detail={renderDetailComponent}
                  dataItemKey={"entryId"}
                  total={userEntries.value?.[selectedTemplateIndex.id]?.length ?? 0}
                  style={{ height: "700px" }}
                  skip={page.skip}
                  take={page.take}
                  pageable={{
                    buttonCount: 5,
                    info: true,
                    type: "input",
                    pageSizes: [25, 50, 100],
                    previousNext: true,
                  }}
                  showLoader={!userEntries.value?.[selectedTemplateIndex.id]}
                  onPageChange={pageChange}
                  rows={{
                    data: (event) => EntryGridRow(event, selectedTemplate),
                  }}
                >
                  {splitEnabledDataset.value?.values?.some(
                    (item) => item.value === templateId.value
                  ) &&
                    columns?.length > 0 && (
                      <GridColumn
                        title="Split Entry"
                        width={100}
                        locked={true}
                        cells={{
                          data: renderSplitIcons,
                        }}
                      />
                    )}
                  {selectedTemplate?.properties?.HasExternalLink && (
                    <GridColumn
                      title="Link Actions"
                      width={120}
                      locked={true}
                      cells={{
                        data: renderLinkButtons,
                      }}
                    />
                  )}
                  {columns}
                  {columns?.length > 0 && (
                    <GridColumn
                      title="Author"
                      field="originalItem.entryAuthor"
                      width={280}
                      columnMenu={ColumnMenu}
                      cells={{
                        data: renderAuthorCell,
                      }}
                    />
                  )}
                  {!hideActionsForSplitEntry && columns?.length > 0 && (
                    <GridColumn
                      title="Actions"
                      width={150}
                      locked={true}
                      cells={{
                        data: (event) =>
                          renderIcons(userIsAdmin, selectedTemplate, userEmail, event, navigate),
                      }}
                    />
                  )}
                </Grid>
                {!loadAllRecords && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "5px",
                    }}
                  >
                    <Button onClick={loadMoreGridRecords}>Load more records</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <EntryPrintPreviewWindow />
      <SplitEntryWindow />
      <EditSplitEntryWindow />
      <CommentsViewWindow />
      <ImageLabelViewWindow />
      {showDeleteEntryDialog.value && (
        <ConfirmDeleteDialog
          content={"entry"}
          handleCancelDelete={() => (showDeleteEntryDialog.value = false)}
          handleConfirmDelete={handleDeleteDialogConfirm}
        />
      )}
      {showRevokeUrlDialog.value && (
        <ConfirmRevokeUrlDialog
          content={"entry"}
          handleCancelRevoke={() => (showRevokeUrlDialog.value = false)}
          handleConfirmRevoke={handleRevokeUrlDialogConfirm}
        />
      )}
    </div>
  );

  // Wrapper function to render DetailComponent with template prop
  function renderDetailComponent(event) {
    return (
      <DetailComponent
        {...event}
        template={selectedTemplate}
      />
    );
  }
};

export default ViewEntry;

// Retrieves field properties from the original entry item by field ID
function getCurrentFieldProperties(field, originalItem) {
  let fieldProperties = {};
  for (const section of originalItem.sections) {
    for (const templateField of section.fields) {
      if (field.fieldId === templateField.fieldId) {
        fieldProperties = templateField.properties;
      }
    }
  }
  return fieldProperties;
}

// Builds template sections with entry values and optional current field properties
function buildTemplateSections(templateValue, originalItem, includeCurrentProps = false) {
  return templateValue.sections.map((section) => ({
    ...section,
    properties: section.properties || {},
    fields: section.fields.map((field) => {
      if (field.fieldType === "Gallery" || field.fieldType === "Table") {
        return {
          ...field,
          value: "",
          properties: {
            ...(includeCurrentProps ? getCurrentFieldProperties(field, originalItem) : {}),
            ...field.properties,
            Values: entryValues.value[field.fieldName] ?? [],
          },
        };
      }
      if (field.properties?.["RevisionCount"]) {
        return {
          ...field,
          value: entryValues.value[field.fieldName] ?? "",
          revisionCount: getRevisionCount(originalItem, field.fieldName),
        };
      }
      return {
        ...field,
        ...(includeCurrentProps
          ? {
              properties: {
                ...getCurrentFieldProperties(field, originalItem),
                ...field.properties,
              },
            }
          : {}),
        value: entryValues.value[field.fieldName] ?? "",
      };
    }),
  }));
}

// Renders action icons (edit, preview, delete) for each grid row
function renderIcons(userIsAdmin, selectedTemplate, userEmail, event, navigate) {
  return (
    <td
      {...event.tdProps}
      className={event.className}
    >
      <div className="align-columns">
        <ColorChangingIcon
          data-testid={"btnEditEntry_" + event.dataIndex}
          icon={faPenLine}
          initialPrimaryColor="#F4A427"
          initialSecondaryColor="#d6562a"
          padding="10px 0 0 0"
          hidden={
            !userCanEditEntry(
              userIsAdmin,
              selectedTemplate,
              userEmail,
              event.dataItem.originalItem.entryAuthor
            )
          }
          onClick={() => {
            formErrors.value = {};
            entryValues.value = getEntryValues(event.dataItem.originalItem);
            template.value = allTemplates.value.find(
              (x) => x.id === event.dataItem.originalItem.templateId
            );
            template.value.sections.forEach((section) => {
              section.fields.forEach((field) => {
                if (
                  (field.fieldType === "Gallery" || field.fieldType === "Table") &&
                  !entryValues.value[field.fieldName]
                ) {
                  setInitialGalleryValues({
                    galleryName: field.fieldName,
                    values: getConnectionData({ field, Data: field.properties?.Data }),
                    fields: field.properties?.Fields,
                    field: field,
                  });
                }
              });
            });
            const updatedSections = buildTemplateSections(
              template.value,
              event.dataItem.originalItem,
              true
            );
            batch(() => {
              templateId.value = event.dataItem.originalItem.templateId;
              template.value = {
                ...event.dataItem.originalItem,
                id: event.dataItem.originalItem.id,
                properties: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).properties,
                templateName: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).templateName,
                sections: updatedSections,
              };
              entryId.value = event.dataItem.originalItem.id;
              steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
              selectedTab.value = 0;
              selectedStep.value = 0;
              initialCommentsLengths.value = {};
              isEntryPreview.value = false;
              readOnlyEntry.value = isApprovalModeEntry(
                selectedTemplate,
                event.dataItem.originalItem.templateId
              );
            });
            validateStageStep();
            navigate(
              `/edit-entry?entryId=${event.dataItem.originalItem.id}&templateId=${event.dataItem.originalItem.templateId}`
            );
          }}
        />
        <ColorChangingIcon
          data-testid={"btnPreviewEntry_" + event.dataIndex}
          icon={faPrintMagnifyingGlass}
          name={"btnPrintWindow_" + template.value?.templateName}
          initialPrimaryColor="#0078D4"
          padding="10px 0 0 0"
          hidden={
            !userCanViewEntry(
              userIsAdmin,
              selectedTemplate,
              userEmail,
              event.dataItem.originalItem.entryAuthor
            )
          }
          onClick={() => {
            entryValues.value = getEntryValues(event.dataItem.originalItem);
            template.value = allTemplates.value.find(
              (x) => x.id === event.dataItem.originalItem.templateId
            );
            const updatedSections = buildTemplateSections(
              template.value,
              event.dataItem.originalItem
            );
            batch(() => {
              templateId.value = event.dataItem.originalItem.templateId;
              template.value = {
                ...event.dataItem.originalItem,
                id: event.dataItem.originalItem.id,
                properties: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).properties,
                templateName: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).templateName,
                sections: updatedSections,
              };
              entryId.value = event.dataItem.originalItem.id;
              steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
              selectedTab.value = 0;
              selectedStep.value = 0;
            });
            showPrintPreviewWindow.value = true;
          }}
        />
        <ColorChangingIcon
          data-testid={"btnDeleteEntry_" + event.dataIndex}
          icon={faTrashCan}
          initialPrimaryColor="#6e6e6e"
          initialSecondaryColor="#919191"
          padding="10px 0 0 0"
          hidden={
            !userCanDeleteEntry(
              userIsAdmin,
              selectedTemplate,
              userEmail,
              event.dataItem.originalItem.entryAuthor
            )
          }
          onClick={() => {
            selectedEntry.value = event.dataItem.originalItem.id;
            showDeleteEntryDialog.value = true;
          }}
        />
      </div>
    </td>
  );
}

// Generates and copies an external link with encrypted token to clipboard
const copyLink = async (event) => {
  const template = userTemplates.value.find((t) => t.id === event.dataItem.originalItem.templateId);

  const datasetId = template?.properties?.HasExternalLink?.datasetId;
  let tokenDataset = getDatasetById(datasetId);

  //get connected field value and adjust for URL use
  const connectedField = template?.properties?.ConnectedField?.fieldName;
  const connectedValue = event.dataItem[connectedField];

  // find token value from dataset values
  const tokenItem = tokenDataset?.values?.find((v) => v.value === event.dataItem.entryId);
  let tokenKey = tokenItem?.filterValue || undefined;
  if (!tokenKey) {
    //generate new token by entryId if one does not exist
    tokenKey = await encryptEntryId(event.dataItem.entryId);
    const newValue = { value: event.dataItem.entryId, filterValue: tokenKey };
    const updatedValues = [...(tokenDataset?.values || []), newValue];
    tokenDataset = { ...tokenDataset, values: updatedValues };

    //store new token in dataset
    const response = await axios.put(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Datasets/${datasetId}?userId=${userEmail}`,
      tokenDataset
    );

    //update existing datasets and make signalR call
    allDatasets.value = allDatasets.value.map((ds) => (ds.id === datasetId ? tokenDataset : ds));
    await handleSignalRTrigger("UPDATE", "Dataset", response);
  }

  //generate URL and copy to clipboard
  const tokenURL = `${
    process.env.REACT_APP_CONTRACTOR_PORTAL_URL
  }/jobs?company=${encodeURIComponent(connectedValue)}&token=${encodeURIComponent(tokenKey)}`;
  navigator.clipboard.writeText(tokenURL);
  alert("Link copied to clipboard");
};

// Revokes an external link by removing the token from the dataset
const revokeLink = async () => {
  const template = userTemplates.value.find((t) => t.id === currentTemplateId.value);
  const datasetId = template?.properties?.HasExternalLink?.datasetId;
  let tokenDataset = getDatasetById(datasetId);
  const tokenItem = tokenDataset?.values?.find((v) => v.value === currentEntryId.value);
  if (!tokenItem) {
    //token does not exist, show error message
    alert("No token found to revoke");
    return;
  }

  //remove token from dataset
  const updatedValues = tokenDataset.values.filter((v) => v.value !== currentEntryId.value);
  tokenDataset = { ...tokenDataset, values: updatedValues };

  //update dataset to remove token
  const response = await axios.put(
    `${process.env.REACT_APP_API_ENDPOINT}/api/Datasets/${datasetId}?userId=${userEmail}`,
    tokenDataset
  );

  //update existing datasets and make signalR call
  allDatasets.value = allDatasets.value.map((ds) => (ds.id === datasetId ? tokenDataset : ds));
  await handleSignalRTrigger("UPDATE", "Dataset", response);
};

// Renders copy/revoke link buttons for entries with external links
function renderLinkButtons(event) {
  const entryId = event.dataItem.entryId;
  const template = userTemplates.value.find((t) => t.id === event.dataItem.originalItem.templateId);

  const datasetId = template?.properties?.HasExternalLink?.datasetId;
  let tokenDataset = getDatasetById(datasetId);

  const hasValidToken = tokenDataset?.values?.some((v) => v.value === entryId);
  return (
    <td
      style={{ ...event.style }}
      className={event.className + "k-border-none"}
    >
      <div className="align-columns-center">
        <ColorChangingIcon
          data-testid={"btnCopyLink"}
          tooltip={`Copy link for ${event.dataItem["Contractor Company Name"]} to clipboard`}
          tooltipPosition={"right"}
          icon={faLinkHorizontal}
          initialPrimaryColor="#0078D4"
          onClick={() => {
            copyLink(event);
          }}
        ></ColorChangingIcon>
        {hasValidToken && (
          <ColorChangingIcon
            data-testid={"btnRevokeLink"}
            tooltip={`Revoke link for ${event.dataItem["Contractor Company Name"]}`}
            tooltipPosition={"right"}
            icon={faLinkHorizontalSlash}
            initialPrimaryColor="#d12a0dff"
            onClick={() => {
              showRevokeUrlDialog.value = true;
              currentTemplateId.value = event.dataItem.originalItem.templateId;
              currentEntryId.value = event.dataItem.entryId;
            }}
          ></ColorChangingIcon>
        )}
      </div>
    </td>
  );
}

// Renders split entry action icons (edit split, create split)
function renderSplitIcons(event) {
  return (
    <td
      style={{ ...event.style }}
      className={event.className}
    >
      <div className="align-columns-center">
        <ColorChangingIcon
          data-testid={"btnSplitEditEntry_" + event.dataIndex}
          icon={faEdit}
          initialPrimaryColor="#0078D4"
          onClick={() => {
            entryValues.value = getEntryValues(event.dataItem.originalItem);
            template.value = allTemplates.value.find(
              (x) => x.id === event.dataItem.originalItem.templateId
            );
            const updatedSections = buildTemplateSections(
              template.value,
              event.dataItem.originalItem
            );
            batch(() => {
              templateId.value = event.dataItem.originalItem.templateId;
              template.value = {
                ...event.dataItem.originalItem,
                id: event.dataItem.originalItem.id,
                properties: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).properties,
                templateName: allTemplates.value.find(
                  (x) => x.id === event.dataItem.originalItem.templateId
                ).templateName,
                sections: updatedSections,
              };
              entryId.value = event.dataItem.originalItem.id;
              steps.value = templateStageSteps.value?.map((step) => ({ label: step.text })) ?? [];
              selectedTab.value = 0;
              selectedStep.value = 0;
              initialCommentsLengths.value = {};
              isEntryPreview.value = false;
            });
            showEditSplitEntryWindow.value = true;
          }}
        ></ColorChangingIcon>
        <ColorChangingIcon
          data-testid={"btnSplitEntry_" + event.dataIndex}
          icon={faSplit}
          initialPrimaryColor="#0078D4"
          onClick={() => {
            batch(() => {
              entryValues.value = {};
              showSplitEntryWindow.value = true;
              entryId.value = event.dataItem.originalItem.id;
              initialCommentsLengths.value = {};
              isEntryPreview.value = false;
            });
          }}
        ></ColorChangingIcon>
      </div>
    </td>
  );
}
